import React, { useEffect, useState, useCallback, useRef, memo } from "react";
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { MdDirections } from 'react-icons/md';
import { SlArrowRight } from "react-icons/sl";
import { AiOutlineMinus, AiOutlinePlus, AiFillStar, AiOutlineShopping, AiOutlineCheckCircle, AiOutlineClose } from 'react-icons/ai';
import { useStateContext } from '../context/StateContext';
import { urlFor } from '../sanity/sanityClient';
import ProductDetail from './ProductDetail';

const SWIPE_THRESHOLD = 40; // px of horizontal drag before a swipe counts

const ACCENT_ADD = '#2563eb';
const ACCENT_BUY = 'white';

/* ===============================
   STYLED COMPONENTS
   (1:1 port of the previous CSS — no visual rules changed)
================================= */

const ProductGrid = styled.div`
  display: grid;
  /* Auto-fills as many ~150px+ columns as fit — naturally gives
     2 columns on most phones and gracefully drops to 1 on very
     narrow screens, with no separate breakpoints needed. */
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  /* Minimal gap — just enough for cards to read as separate
     tiles without wasting screen space on whitespace. Since the
     grid itself is white (same as the cards), this reads as a
     faint seam rather than a visible gray line from the page
     background showing through. */
  gap: 1px;
  padding: 1px;
  background: #ffffff;
`;

const ProductCardWrapper = styled.div`
  background: white;
  border-radius: 4px;
  /* No shadow/border — cards sit flush against each other and
     the page background, so the 1px grid gap is the only
     visual separation between tiles. */
  box-shadow: none;
  border: none;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: background-color 120ms ease, transform 120ms ease;

  &:active {
    transform: scale(0.98);
    background-color: #fafafa;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

const ActionBar = styled.div`
  display: flex;
  gap: 4px;
  padding: 6px 6px 0;
`;

const ACTION_BUTTON_BASE = css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 4px;
  border: none;
  border-radius: 20px;
  font-size: 10.5px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  background: #f8fafc;
  white-space: nowrap;
  touch-action: manipulation;
`;

const DirectionButton = styled.button`
  ${ACTION_BUTTON_BASE}
  color: #3b82f6;
`;

const MessageButton = styled(Link)`
  ${ACTION_BUTTON_BASE}
  color: #2e7d32;
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  margin-top: 6px;
  background: #f2f2f2;
  touch-action: pan-y; /* keep vertical page scroll working alongside horizontal swipe */
  cursor: pointer;
`;

const StatusBadge = styled.span`
  position: absolute;
  top: 6px;
  left: 6px;
  padding: 3px 8px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.02em;
  border-radius: 12px;
  color: white;
  z-index: 2;
  text-transform: uppercase;
  background: ${({ $status }) =>
    $status === 'available'
      ? 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)'
      : 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'};
`;

const ImageCounter = styled.span`
  position: absolute;
  bottom: 8px;
  right: 8px;
  padding: 2px 8px;
  font-size: 10px;
  font-weight: 600;
  color: white;
  background: rgba(0, 0, 0, 0.55);
  border-radius: 10px;
  z-index: 2;
  letter-spacing: 0.02em;
`;

const MainImage = styled.img`
  /* Plain native <img>, absolutely positioned to fill ImageWrapper
     edge-to-edge. A manual opacity fade (driven by onLoad, see
     ProductCard) replaces the library's blur effect — this avoids
     react-lazy-load-image-component's wrapper <span>, which was
     receiving our styles while the actual <img> inside it stayed
     unstyled and stuck at opacity: 0. */
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  opacity: ${({ $loaded }) => ($loaded ? 1 : 0)};
  transition: opacity 250ms ease;
`;

const ThumbnailRow = styled.div`
  display: flex;
  gap: 5px;
  overflow-x: auto;
  padding: 7px 6px;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
`;

const ThumbBtn = styled.button`
  flex: 0 0 auto;
  padding: 0;
  border: 2px solid transparent;
  border-radius: 6px;
  overflow: hidden;
  background: none;
  cursor: pointer;
  line-height: 0;
  opacity: 0.7;
  transition: opacity 150ms ease, border-color 150ms ease, transform 150ms ease;

  ${({ $active }) =>
    $active &&
    css`
      border-color: ${ACCENT_ADD};
      opacity: 1;
      transform: scale(1.05);
    `}

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

const Thumb = styled.img`
  width: 36px;
  height: 36px;
  object-fit: cover;
  display: block;
`;

const ProductInfo = styled.div`
  padding: 2px 8px 10px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;

  h4 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    color: #1e293b;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    max-height: calc(1.3em * 2);
  }
`;

const Price = styled.p`
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  color: #111;
  line-height: 1.2;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #64748b;
`;

const Rating = styled.span`
  display: flex;
  align-items: center;
  gap: 2px;
  color: #f59e0b;
  font-weight: 600;

  svg {
    font-size: 12px;
  }
`;

const LocationText = styled.span`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const Details = styled.p`
  margin: 0;
  font-size: 11.5px;
  line-height: 1.35;
  color: #64748b;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  max-height: calc(1.35em * 2);
`;

const QtyRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 2px;
`;

const QtyLabel = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 600;
`;

const QtyControl = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f8fafc;
  padding: 2px 4px;
  border-radius: 14px;

  span {
    min-width: 14px;
    text-align: center;
    font-size: 12px;
    font-weight: 600;
  }
`;

const QtyButton = styled.button`
  border: none;
  background: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  touch-action: manipulation;
`;

const ProductActions = styled.div`
  display: flex;
  gap: 6px;
  margin-top: 4px;
`;

const ACTION_CTA_BASE = css`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 4px;
  border: none;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  min-height: 34px;
  touch-action: manipulation;
  transition: transform 120ms ease;

  &:active {
    transform: scale(0.95);
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

const AddToCartBtn = styled.button`
  ${ACTION_CTA_BASE}
  background: rgba(37, 99, 235, 0.1);
  color: ${ACCENT_ADD};
`;

const BuyNowBtn = styled.button`
  ${ACTION_CTA_BASE}
  background: ${ACCENT_BUY};
  color: linear-gradient(135deg, #000000 0%, #1a1a1a 35%, #2b2b2b 50%, #111111 75%, #000000 100%);
  box-shadow: 0 3px 8px rgba(22, 163, 74, 0.25);
`;

const GridEndMessage = styled.div`
  text-align: center;
  padding: 16px 0 24px;
  color: #94a3b8;
  font-size: 12.5px;
`;

const pdSlideDown = css`
  @keyframes pdSlideDown {
    from {
      opacity: 0;
      transform: translate(-50%, -60%);
    }
    to {
      opacity: 1;
      transform: translate(-50%, -50%);
    }
  }
`;

const Toast = styled.div`
  ${pdSlideDown}
  position: fixed;
  top: 18%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.9);
  backdrop-filter: blur(8px);
  color: white;
  padding: 12px 20px;
  border-radius: 30px;
  display: flex;
  align-items: center;
  gap: 10px;
  z-index: 1000;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  animation: pdSlideDown 0.3s ease;

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

const ToastTitle = styled.div`
  font-weight: 600;
  font-size: 13px;
`;

const ToastMessage = styled.div`
  font-size: 11.5px;
  opacity: 0.9;
`;

/* ---- Full-screen product detail overlay, opened by tapping a card's image ---- */

const DetailOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  width: 100vw;
  height: 100vh;
  height: 100dvh; /* avoids mobile browser chrome jump */
  background: white;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  animation: pdSlideUp 220ms ease;

  @keyframes pdSlideUp {
    from {
      opacity: 0;
      transform: translateY(24px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
  }
`;

const DetailCloseButton = styled.button`
  position: fixed;
  top: max(12px, env(safe-area-inset-top, 0px));
  right: 12px;
  z-index: 2001;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(26, 26, 26, 0.82);
  backdrop-filter: blur(6px);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.25);
  touch-action: manipulation;

  &:active {
    transform: scale(0.92);
  }
`;

/* ===============================
   COMPONENTS (unchanged logic, plus tap-to-open-detail)
================================= */

/**
 * Memoized card. Quantity/image-index live on the product object in
 * displayProducts, so only the card whose product actually changed
 * re-renders — bumping one item's quantity no longer re-renders the
 * whole grid.
 */
const ProductCard = memo(function ProductCard({
  product,
  onImageSelect,
  onIncQty,
  onDecQty,
  onAddToCart,
  onBuyNow,
  onGetDirections,
  onOpenDetail,
}) {
  const touchStartXRef = useRef(null);
  // Set when a touch gesture actually crosses the swipe threshold, so the
  // synthetic click that follows a drag's touchend doesn't also open the
  // detail overlay — only a genuine tap should do that.
  const didSwipeRef = useRef(false);
  const imageCount = product.image.length;
  const currentIndex = product.currentImageIndex;

  // Drives MainImage's fade-in. Resets whenever the shopper swipes or
  // taps a thumbnail to a different photo, so each new image fades in
  // on its own load rather than staying at the previous opacity.
  const [mainImageLoaded, setMainImageLoaded] = useState(false);
  useEffect(() => {
    setMainImageLoaded(false);
  }, [currentIndex]);

  const goToImage = useCallback((index) => {
    const wrapped = ((index % imageCount) + imageCount) % imageCount;
    onImageSelect(product._id, wrapped);
  }, [imageCount, onImageSelect, product._id]);

  const handleTouchStart = useCallback((e) => {
    touchStartXRef.current = e.touches[0].clientX;
  }, []);

  const handleTouchEnd = useCallback((e) => {
    if (touchStartXRef.current === null) {
      didSwipeRef.current = false;
      return;
    }
    const deltaX = e.changedTouches[0].clientX - touchStartXRef.current;
    touchStartXRef.current = null;

    if (imageCount > 1 && Math.abs(deltaX) >= SWIPE_THRESHOLD) {
      didSwipeRef.current = true;
      goToImage(currentIndex + (deltaX < 0 ? 1 : -1));
    } else {
      didSwipeRef.current = false;
    }
  }, [currentIndex, goToImage, imageCount]);

  // Tapping the image (not a swipe) opens the full-screen detail view.
  const handleImageTap = useCallback(() => {
    if (didSwipeRef.current) {
      didSwipeRef.current = false;
      return;
    }
    onOpenDetail(product);
  }, [onOpenDetail, product]);

  return (
    <ProductCardWrapper>
      {/* ACTION BAR */}
      {(product.geolocation || product.user) && (
        <ActionBar>
          {product.geolocation && (
            <DirectionButton onClick={() => onGetDirections(product.geolocation)}>
              <MdDirections size={14} />
              Directions
            </DirectionButton>
          )}

          {product.user && (
            <MessageButton
              to={{
                pathname: `/chat/${product.user}`,
                state: { productImage: product.image[0].asset._ref }
              }}
            >
              Message
              <SlArrowRight size={11} />
            </MessageButton>
          )}
        </ActionBar>
      )}

      {/* IMAGE — swipeable when there's more than one photo, tap opens full detail */}
      <ImageWrapper
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClick={handleImageTap}
        role="button"
        tabIndex={0}
        aria-label={`View details for ${product.name}`}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onOpenDetail(product);
          }
        }}
      >
        {product.status && (
          <StatusBadge $status={product.status}>
            {product.status}
          </StatusBadge>
        )}

        {imageCount > 1 && (
          <ImageCounter>
            {currentIndex + 1}/{imageCount}
          </ImageCounter>
        )}

        <MainImage
          key={currentIndex}
          src={urlFor(product.image[currentIndex])
            .width(400)
            .height(400)
            .auto('format')
            .quality(70)
            .url()}
          alt={product.name}
          loading="lazy"
          decoding="async"
          onLoad={() => setMainImageLoaded(true)}
          $loaded={mainImageLoaded}
        />
      </ImageWrapper>

      {/* THUMBNAILS — always shown, mirrors the full gallery on the image */}
      <ThumbnailRow>
        {product.image.map((img, index) => (
          <ThumbBtn
            key={index}
            type="button"
            $active={index === currentIndex}
            onClick={() => onImageSelect(product._id, index)}
            aria-label={`View image ${index + 1} of ${product.name}`}
            aria-current={index === currentIndex}
          >
            <Thumb
              src={urlFor(img).width(90).height(90).auto('format').quality(55).url()}
              alt=""
              loading="lazy"
              decoding="async"
            />
          </ThumbBtn>
        ))}
      </ThumbnailRow>

      {/* INFO */}
      <ProductInfo>
        <h4>{product.name}</h4>

        <Price>
          {product.currency || 'Tzs'} {product.price?.toLocaleString()}/=
        </Price>

        <MetaRow>
          <Rating>
            <AiFillStar />
            <span>4.0</span>
          </Rating>
          {product.location && <LocationText>{product.location}</LocationText>}
        </MetaRow>

        {product.details && <Details>{product.details}</Details>}

        <QtyRow>
          <QtyLabel>Qty</QtyLabel>
          <QtyControl>
            <QtyButton
              type="button"
              aria-label="Decrease quantity"
              onClick={() => onDecQty(product._id)}
            >
              <AiOutlineMinus />
            </QtyButton>
            <span>{product.quantity || 1}</span>
            <QtyButton
              type="button"
              aria-label="Increase quantity"
              onClick={() => onIncQty(product._id)}
            >
              <AiOutlinePlus />
            </QtyButton>
          </QtyControl>
        </QtyRow>

        {/* ACTION BUTTONS */}
        <ProductActions>
          <AddToCartBtn onClick={() => onAddToCart(product)}>
            <AiOutlineShopping size={15} />
            Add
          </AddToCartBtn>

          <BuyNowBtn onClick={() => onBuyNow(product)}>
            Buy Now
          </BuyNowBtn>
        </ProductActions>
      </ProductInfo>
    </ProductCardWrapper>
  );
});

const ProductDisplay = ({ products, loadMoreProducts, hasMore, loading }) => {

  const { setShowCart, onAdd } = useStateContext();

  const [displayProducts, setDisplayProducts] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastProduct, setToastProduct] = useState(null);

  // The product currently shown full-screen, or null when the overlay is closed.
  const [selectedProduct, setSelectedProduct] = useState(null);

  // Tracks which product IDs are already on screen so a `products`
  // update only shuffles-in the NEW items, instead of re-shuffling
  // (and visually jumbling) everything the shopper has already
  // scrolled past.
  const shownIdsRef = useRef(new Set());
  const sentinelRef = useRef(null);

  /* ------------------ Infinite Scroll (IntersectionObserver) ------------------
     A sentinel + observer avoids running a scroll-position calculation
     on every scroll event; it only fires when the sentinel is close
     to entering the viewport. */
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loading && hasMore && loadMoreProducts) {
          loadMoreProducts();
        }
      },
      { rootMargin: '600px 0px' }
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [loading, hasMore, loadMoreProducts]);

  useEffect(() => {
    if (!products || products.length === 0) {
      setDisplayProducts([]);
      shownIdsRef.current = new Set();
      return;
    }

    const incomingIds = new Set(products.map((p) => p._id));
    const isFreshList = ![...shownIdsRef.current].some((id) => incomingIds.has(id));

    const shuffle = (arr) => {
      for (let i = arr.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [arr[i], arr[j]] = [arr[j], arr[i]];
      }
      return arr;
    };

    if (isFreshList) {
      // A genuinely new set (e.g. the category/slug changed) — shuffle
      // it fresh and replace the grid entirely.
      const shuffled = shuffle(
        products.map((product) => ({
          ...product,
          currentImageIndex: product.currentImageIndex || 0,
          quantity: 1,
        }))
      );
      shownIdsRef.current = incomingIds;
      setDisplayProducts(shuffled);
      return;
    }

    // Same list, more items arrived — keep already-shown cards exactly
    // where they are and only shuffle-in the new batch at the end.
    const newItems = products
      .filter((p) => !shownIdsRef.current.has(p._id))
      .map((product) => ({
        ...product,
        currentImageIndex: product.currentImageIndex || 0,
        quantity: 1,
      }));

    if (newItems.length === 0) return;

    shuffle(newItems);
    newItems.forEach((p) => shownIdsRef.current.add(p._id));
    setDisplayProducts((prev) => [...prev, ...newItems]);
  }, [products]);

  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
        setToastProduct(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // Lock page scroll while the full-screen detail overlay is open, so the
  // grid underneath doesn't scroll along with it.
  useEffect(() => {
    document.body.style.overflow = selectedProduct ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [selectedProduct]);

  const handleImageSelect = useCallback((productId, index) => {
    setDisplayProducts((prevProducts) =>
      prevProducts.map((product) =>
        product._id === productId
          ? { ...product, currentImageIndex: index }
          : product
      )
    );
  }, []);

  const handleIncQty = useCallback((productId) => {
    setDisplayProducts((prevProducts) =>
      prevProducts.map((product) =>
        product._id === productId
          ? { ...product, quantity: (product.quantity || 1) + 1 }
          : product
      )
    );
  }, []);

  const handleDecQty = useCallback((productId) => {
    setDisplayProducts((prevProducts) =>
      prevProducts.map((product) =>
        product._id === productId
          ? { ...product, quantity: Math.max(1, (product.quantity || 1) - 1) }
          : product
      )
    );
  }, []);

  const handleAddToCart = useCallback((product) => {
    const productWithSelectedImage = {
      ...product,
      image: [product.image[product.currentImageIndex]],
      quantity: product.quantity || 1
    };

    onAdd(productWithSelectedImage, product.quantity || 1);

    setToastProduct({
      name: product.name,
      quantity: product.quantity || 1
    });
    setShowToast(true);
  }, [onAdd]);

  const handleBuyNow = useCallback((product) => {
    const productWithSelectedImage = {
      ...product,
      image: [product.image[product.currentImageIndex]],
      quantity: product.quantity || 1
    };

    onAdd(productWithSelectedImage, product.quantity || 1);
    setShowCart(true);
  }, [onAdd, setShowCart]);

  const handleGetDirections = useCallback((productLocation) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLatitude = position.coords.latitude;
          const userLongitude = position.coords.longitude;
          const directionsUrl =
            `https://www.google.com/maps/dir/?api=1&origin=${userLatitude},${userLongitude}&destination=${productLocation.lat},${productLocation.lng}&travelmode=driving`;
          window.open(directionsUrl, "_blank");
        },
        () => alert("Unable to fetch your location. Please allow location access.")
      );
    }
  }, []);

  const handleOpenDetail = useCallback((product) => {
    setSelectedProduct(product);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedProduct(null);
  }, []);

  return (
    <>
      {showToast && (
        <Toast>
          <AiOutlineCheckCircle size={22} color="#4caf50" />
          <div>
            <ToastTitle>Added to Cart</ToastTitle>
            <ToastMessage>
              {toastProduct?.quantity} x {toastProduct?.name}
            </ToastMessage>
          </div>
        </Toast>
      )}

      <ProductGrid>
        {displayProducts.map((product) => (
          <ProductCard
            key={product._id}
            product={product}
            onImageSelect={handleImageSelect}
            onIncQty={handleIncQty}
            onDecQty={handleDecQty}
            onAddToCart={handleAddToCart}
            onBuyNow={handleBuyNow}
            onGetDirections={handleGetDirections}
            onOpenDetail={handleOpenDetail}
          />
        ))}
      </ProductGrid>

      {/* Sentinel for IntersectionObserver-driven infinite scroll */}
      <div ref={sentinelRef} style={{ height: 1 }} />

      {loading && <GridEndMessage>Loading products…</GridEndMessage>}

      {/* Full-screen product detail, opened by tapping a card's image */}
      {selectedProduct && (
        <DetailOverlay>
          <DetailCloseButton
            onClick={handleCloseDetail}
            aria-label="Close product details"
          >
            <AiOutlineClose size={18} />
          </DetailCloseButton>
          <ProductDetail products={[selectedProduct]} />
        </DetailOverlay>
      )}
    </>
  );
};

export default ProductDisplay;
